import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { PdfUpload } from '../../components/common/PdfUpload';
import { TeamDropdown } from '../../components/common/TeamDropdown';
import { fetchPlayerTeams, loadMorePlayerTeams } from '../../features/team/teamSlice';
import { register, clearError } from '../../features/auth/authSlice';

const inputClass =
  'w-full px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition text-sm';

export const AdditionRequestPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { teams, playerTeamsPagination, playerTeamsLoading } = useSelector((state) => state.team);
  const { loading: authLoading, error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    team: null,
    ncaaId: '',
    xURL: '',
    instaURL: ''
  });
  const [photoIdDocument, setPhotoIdDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const searchTermRef = useRef('');

  useEffect(() => {
    // Clear error when component mounts
    dispatch(clearError());
    dispatch(fetchPlayerTeams({ page: 1, limit: 20 }));

    // Clear error when component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Handle search - called from TeamDropdown with debounce
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    searchTermRef.current = term;
    dispatch(fetchPlayerTeams({ page: 1, limit: 20, teamName: term || undefined }));
  }, [dispatch]);

  // Handle load more - called when user scrolls to bottom
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !playerTeamsPagination.hasMore) return;

    setLoadingMore(true);
    const nextPage = playerTeamsPagination.currentPage + 1;
    dispatch(loadMorePlayerTeams({
      page: nextPage,
      limit: 20,
      teamName: searchTermRef.current || undefined
    })).finally(() => {
      setLoadingMore(false);
    });
  }, [dispatch, loadingMore, playerTeamsPagination]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTeamSelect = (team) => {
    setFormData({ ...formData, team });
  };

  const handleFileChange = (file) => {
    setPhotoIdDocument(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.team) {
      return;
    }

    const teamId = formData.team.id || formData.team._id;

    // Create FormData object for multipart/form-data submission
    const submitData = new FormData();
    submitData.append('firstName', formData.firstName);
    submitData.append('lastName', formData.lastName);
    submitData.append('email', formData.email);
    submitData.append('phoneNumber', formData.phoneNumber);
    submitData.append('teamId', teamId);

    // Optional fields
    if (formData.ncaaId) {
      submitData.append('ncaaId', formData.ncaaId);
    }
    if (formData.xURL) {
      submitData.append('xURL', formData.xURL);
    }
    if (formData.instaURL) {
      submitData.append('instaURL', formData.instaURL);
    }

    if (photoIdDocument) {
      submitData.append('photoIdDocument', photoIdDocument);
    }

    const resultAction = await dispatch(register(submitData));

    if (register.fulfilled.match(resultAction)) {
      navigate('/auth/waiting-verification');
    }
  };

  const textField = (id, label, name, { type = 'text', placeholder = '', autoComplete, optional = false } = {}) => (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-jp-ink mb-1.5">
        {label} {optional && <span className="text-jp-muted text-xs font-normal">(Optional)</span>}
      </label>
      <input
        id={id}
        type={type}
        name={name}
        autoComplete={autoComplete}
        value={formData[name]}
        onChange={handleChange}
        required={!optional}
        placeholder={placeholder}
        className={inputClass}
      />
    </div>
  );

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Navy strip behind the see-through navbar so its white logo and links stay readable */}
      <div className="bg-jp-band h-16 sm:h-20" aria-hidden="true" />

      <section className="px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-20">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <p className="text-jp-accent dark:text-jp-gold text-sm font-bold uppercase tracking-widest mb-3">
            For Players
          </p>
          <h1 className="text-5xl sm:text-6xl font-anton leading-[1.1] tracking-tight text-jp-ink">
            ADDITION REQUEST FORM
          </h1>
          <p className="mt-4 text-jp-muted text-sm sm:text-base">
            Not on your team's list? Send us your details and we'll add you.
          </p>
        </div>

        <div className="max-w-3xl mx-auto bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-5 sm:p-8">
          <form onSubmit={handleSubmit} className="rounded-xl p-5 sm:p-6 bg-jp-section border border-jp-outline/50 space-y-5">
            {error && (
              <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {textField('addition-first-name', 'First name', 'firstName', { autoComplete: 'given-name' })}
              {textField('addition-last-name', 'Last name', 'lastName', { autoComplete: 'family-name' })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {textField('addition-email', 'Email', 'email', { type: 'email', autoComplete: 'email', placeholder: 'you@example.com' })}
              {textField('addition-phone', 'Phone number', 'phoneNumber', { type: 'tel', autoComplete: 'tel', placeholder: '(555) 555-5555' })}
            </div>

            {/* Team Dropdown */}
            <div>
              <span className="block text-sm font-semibold text-jp-ink mb-1.5">Your team</span>
              <TeamDropdown
                selectedTeam={formData.team}
                onSelectTeam={handleTeamSelect}
                teams={teams || []}
                loading={playerTeamsLoading && teams.length === 0}
                loadingText="Loading teams..."
                placeholder="Select team"
                variant="themed"
                onSearch={handleSearch}
                onLoadMore={handleLoadMore}
                hasMore={playerTeamsPagination?.hasMore || false}
                loadingMore={loadingMore}
              />
            </div>

            {textField('addition-ncaa-id', 'NCAA ID', 'ncaaId', { optional: true, placeholder: 'Enter your NCAA ID' })}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {textField('addition-x-url', 'X (Twitter) URL', 'xURL', { type: 'url', optional: true, placeholder: 'https://x.com/username' })}
              {textField('addition-insta-url', 'Instagram URL', 'instaURL', { type: 'url', optional: true, placeholder: 'https://instagram.com/username' })}
            </div>

            {/* Photo ID Document Upload */}
            <PdfUpload
              label="Photo ID"
              file={photoIdDocument}
              onFileChange={handleFileChange}
              accept="image/*,.pdf"
              placeholder="Upload a photo or PDF of your ID so we can verify it's you"
              themed
            />

            <div className="max-w-md mx-auto pt-3">
              <button
                type="submit"
                disabled={authLoading || playerTeamsLoading}
                className="w-full py-4 text-base font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-section active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {authLoading ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </form>

          <p className="px-2 pt-5 pb-1 text-center text-sm sm:text-base text-jp-muted">
            Found your name after all?{' '}
            <Link to="/auth/login-player" className="font-semibold text-jp-accent hover:underline">
              Claim your player profile
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
};

export default AdditionRequestPage;
